import { useQueries } from '@tanstack/react-query';

import { connectorService } from '@/services/connector-service';
import type { ConnectorInvocationStatus } from '@/types/core/connector-invocation-status';
import type { UUID } from '@/utils/uuid';

import { connectorQueryKeys } from './query-keys';

/**
 * For each feature that has a known current run sequence > 1, fetches the
 * previous run (currentSequence - 1).
 */
export function useGetPreviousConnectorFeatureInvocationStatuses(
  tenantId: UUID | undefined,
  connectorId: UUID | undefined,
  currentSequenceByFeatureId: Record<UUID, number>,
  opts?: { enabled?: boolean }
) {
  const enabled = opts?.enabled !== false && !!tenantId && !!connectorId;

  const entries = (
    Object.entries(currentSequenceByFeatureId) as [UUID, number][]
  ).filter(([, seq]) => seq > 1);

  const results = useQueries({
    queries: entries.map(([featureId, currentSeq]) => ({
      queryKey: connectorQueryKeys.invocationStatus(
        tenantId,
        connectorId,
        featureId,
        currentSeq - 1
      ),
      queryFn: async (): Promise<ConnectorInvocationStatus> => {
        if (!tenantId || !connectorId) throw new Error('Missing ids');
        const result = await connectorService.findConnectorInvocationStatus(
          tenantId,
          connectorId,
          featureId,
          currentSeq - 1
        );
        // Throw if not found — the backend may need a moment to make a
        // recently-completed invocation available via the individual lookup
        // endpoint (eventual consistency). Retries will pick it up.
        if (!result) throw new Error('Previous invocation not available yet');
        return result;
      },
      enabled: enabled && currentSeq > 1,
      retry: 5,
      retryDelay: (attempt: number) => Math.min(1000 * 2 ** attempt, 8000),
    })),
  });

  const previousStatuses: Record<UUID, ConnectorInvocationStatus> = {};
  entries.forEach(([featureId], index) => {
    const result = results[index];
    if (result?.data) {
      previousStatuses[featureId] = result.data;
    }
  });

  return { previousStatuses };
}
